import { useMediaQuery, useTheme } from "@mui/material";
import { useMemo } from "react";

/**
 * A custom hook that returns a function to get responsive values based on screen size.
 * The returned function takes an array of values and returns the appropriate value based on the current screen size.
 *
 * @returns A function that takes an array of values and returns the appropriate value based on screen size
 * @example
 * const getResponsiveValue = useResponsiveValue();
 * const size = getResponsiveValue([100, 200, 300, 400]); // Returns 100 on mobile, 200 on tablet, 300 on desktop, 400 on large desktop
 */
export const useResponsiveValue = () => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.between("lg", "xl"));
  const isExtraLargeDesktop = useMediaQuery(theme.breakpoints.up("xl"));

  return useMemo(
    () =>
      <T>(
        values: [T] | [T, T] | [T, T, T] | [T, T, T, T] | [T, T, T, T, T],
      ): T => {
        if (isMobile) return values[0];
        if (isTablet) return values[1] ?? values[0];
        if (isDesktop) return values[2] ?? values[1] ?? values[0];
        if (isLargeDesktop)
          return values[3] ?? values[2] ?? values[1] ?? values[0];
        if (isExtraLargeDesktop)
          return values[4] ?? values[3] ?? values[2] ?? values[1] ?? values[0];
        return values[0];
      },
    [isMobile, isTablet, isDesktop, isLargeDesktop, isExtraLargeDesktop],
  );
};
